import { useEffect, useRef, useState } from "react";

export interface Prefill {
  imageUrl: string;
  fileName: string;
}

interface Handlers {
  /** Called with the loaded original wrapped as a File. */
  onFile: (file: File) => void;
  /** Called with a human-readable message when loading fails. */
  onError: (message: string) => void;
}

/**
 * Loads a prefilled original image (passed via router state from the gallery's
 * "Re-generate"/"Serverless" actions) into a `File` and hands it to the page. Both
 * Generate flows share this identically: fetch the blob by URL, wrap it as a File
 * with the given name and the response's content type, and report loading state. Aborts on unmount or when the
 * source changes. Returns whether the prefill image is currently loading.
 */
export function usePrefillFile(prefill: Prefill | null, handlers: Handlers): boolean {
  const [loading, setLoading] = useState(false);

  // Keep the latest handlers in a ref so the load effect depends only on the source
  // image, not on handler identity (which changes every render).
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  const imageUrl = prefill === null ? null : prefill.imageUrl;
  const fileName = prefill === null ? null : prefill.fileName;

  useEffect(() => {
    if (imageUrl === null || fileName === null) return;
    // Narrowed copies for the async closure below.
    const url = imageUrl;
    const name = fileName;

    const controller = new AbortController();

    async function load() {
      setLoading(true);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error("Failed to load original image");

        const blob = await response.blob();
        if (blob.type === "") throw new Error("Original image response has no content type");
        const file = new File([blob], name, { type: blob.type });
        handlersRef.current.onFile(file);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        handlersRef.current.onError(err instanceof Error ? err.message : "Failed to load original image");
      } finally {
        setLoading(false);
      }
    }

    load();

    return () => controller.abort();
  }, [imageUrl, fileName]);

  return loading;
}
